import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'

import { FormHandles } from '@unform/core'
import {
  ModalBase,
  Botao,
  FormUnform,
  InputUnform,
  InputMesAnoUnform,
  CartaoCreditoInputUnform,
  IconeInformacao,
  Tooltip,
  Cores,
  IconeCartaoVisa,
  IconeCartaoMaster,
  IconeCartaoAmex,
  IconeCartaoElo,
  IconeCartaoHiper,
  IconeCartaoJcb,
  IconeCartaoDiners,
  regExpVisa,
  regExpMaster,
  regExpAmex,
  regExpElo,
  regExpHiper,
  regExpJcb,
  regExpDiners
} from 'src/componentes'
import { DadosCartao } from 'src/servicos/api/tipos'

import {
  schemaPagamentoCartaoCredito,
  schemaPagamentoCartaoCreditoVisaMasterElo
} from './schema'
import {
  ContainerAcoes,
  ContainerBandeiras,
  ContainerDados,
  ContainerDadosMedio,
  ContainerModal,
  IconeLabel,
  LinhaBandeiras,
  CorpoModal
} from './styles'
import { ModalPagamentoProps, ModalPagamentoRef } from './tipos'

const ModalPagamentoRecorrente: React.ForwardRefRenderFunction<
  ModalPagamentoRef,
  ModalPagamentoProps
> = (
  { backdrop, info, acaoPrimaria, apenasBandeirasVisaMasterElo = false },
  ref
) => {
  const [aberta, definirAberta] = useState(false)
  const [numeroCartao, definirNumeroCartao] = useState('')
  const [carregando, definirCarregando] = useState(false)
  const formRef = useRef<FormHandles>(null)
  const [pronto, definirPronto] = useState(false)

  const schemaFormulario = useMemo(
    () =>
      apenasBandeirasVisaMasterElo
        ? schemaPagamentoCartaoCreditoVisaMasterElo
        : schemaPagamentoCartaoCredito,
    [apenasBandeirasVisaMasterElo]
  )

  const fechar = () => {
    definirPronto(false)
    definirAberta(false)
  }

  const abrir = () => {
    definirPronto(true)
    definirAberta(true)
  }

  useImperativeHandle<ModalPagamentoRef, ModalPagamentoRef>(ref, () => ({
    fechar,
    abrir
  }))

  const dadosIniciais = useMemo(() => {
    return {
      nomeImpresso: null,
      numero: null,
      validade: null,
      codigo: null,
      parcelas: null
    }
  }, [])

  const acaoSucessoForm = async (dados: DadosCartao) => {
    const payload = {
      ...dados,
      parcelas: 1
    }
    acaoPrimaria(payload)
    definirCarregando(true)
  }

  return (
    <ModalBase id="modal-pagamento" aberta={aberta} backdrop={backdrop}>
      <CorpoModal>
        <ContainerModal>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '15px',
              gap: '10px'
            }}
          >
            <span style={{ fontSize: '1.80rem' }}>
              Cartão pagamento recorrente
            </span>
            {info && (
              <div
                style={{
                  marginBottom: '15px',
                  fontWeight: 500,
                  fontSize: '10px'
                }}
              >
                <span style={{ color: '#FF156B' }}>Importante:</span> A data da
                primeira cobrança será no dia de vencimento da parcela que
                consta em seu painel financeiro.
              </div>
            )}
          </div>
          {pronto && (
            <FormUnform
              acaoSucesso={acaoSucessoForm}
              schema={schemaFormulario}
              ref={formRef}
              dadosIniciais={dadosIniciais}
            >
              <LinhaBandeiras>
                <ContainerBandeiras
                  selecionado={
                    !!numeroCartao.match(regExpVisa) &&
                    !numeroCartao.match(regExpElo)
                  }
                  temDataVencimento={apenasBandeirasVisaMasterElo}
                >
                  {IconeCartaoVisa}
                </ContainerBandeiras>
                <ContainerBandeiras
                  selecionado={!!numeroCartao.match(regExpMaster)}
                  temDataVencimento={apenasBandeirasVisaMasterElo}
                >
                  {IconeCartaoMaster}
                </ContainerBandeiras>
                <ContainerBandeiras
                  selecionado={!!numeroCartao.match(regExpElo)}
                  temDataVencimento={apenasBandeirasVisaMasterElo}
                >
                  {IconeCartaoElo}
                </ContainerBandeiras>
                {!apenasBandeirasVisaMasterElo && (
                  <>
                    <ContainerBandeiras
                      selecionado={!!numeroCartao.match(regExpAmex)}
                    >
                      {IconeCartaoAmex}
                    </ContainerBandeiras>
                    <ContainerBandeiras
                      selecionado={!!numeroCartao.match(regExpHiper)}
                    >
                      {IconeCartaoHiper}
                    </ContainerBandeiras>
                    <ContainerBandeiras
                      selecionado={!!numeroCartao.match(regExpJcb)}
                    >
                      {IconeCartaoJcb}
                    </ContainerBandeiras>
                    <ContainerBandeiras
                      selecionado={!!numeroCartao.match(regExpDiners)}
                    >
                      {IconeCartaoDiners}
                    </ContainerBandeiras>
                  </>
                )}
              </LinhaBandeiras>
              <CartaoCreditoInputUnform
                id="ipt_numero"
                name="numero"
                label="Número do Cartão"
                obrigatorio
                onChange={e => {
                  const valor = e.target.value.replaceAll(' ', '')
                  definirNumeroCartao(valor)
                }}
              />
              <InputUnform
                id="ipt_nome_impresso"
                name="nomeImpresso"
                label="Nome Conforme Consta no Cartão"
                obrigatorio
              />
              <ContainerDados>
                <ContainerDadosMedio>
                  <InputMesAnoUnform
                    id="ipt_validade"
                    name="validade"
                    label="Validade do Cartão"
                    placeholder="mm/aaaa"
                    formatoAno="AAAA"
                    obrigatorio
                  />
                  <Tooltip
                    id="tooltip-cvv"
                    place="right"
                    className="tooltip-ipgs"
                    textColor={Cores.PRETO}
                    backgroundColor={Cores.CINZA_1_CLARO}
                  />
                </ContainerDadosMedio>
                <ContainerDadosMedio>
                  <InputUnform
                    id="ipt_codigo"
                    name="codigo"
                    label={
                      <>
                        Código de Segurança
                        <IconeLabel
                          data-tip="O código de segurança, ou CVV, refere-se ao código de 3 dígitos exibido na frente ou no verso do seu cartão."
                          data-for="tooltip-cvv"
                        >
                          {IconeInformacao}
                        </IconeLabel>
                      </>
                    }
                    obrigatorio
                    maxLength={4}
                  />
                </ContainerDadosMedio>
              </ContainerDados>
              <ContainerAcoes>
                <Botao
                  texto="Cancelar"
                  tema="Secundario"
                  type="button"
                  disabled={carregando}
                  onClick={() => fechar()}
                />
                <Botao texto="Enviar" carregando={carregando} />
              </ContainerAcoes>
            </FormUnform>
          )}
        </ContainerModal>
      </CorpoModal>
    </ModalBase>
  )
}

export const ModalPagamentoCartaoRecorrente = forwardRef(
  ModalPagamentoRecorrente
)
